import { useState, useEffect, useReducer, useRef, lazy, Suspense } from 'react'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import AddSheetsWizard from './AddSheetsWizard.jsx'
import DrawingSheetList, { getDiscipline, SheetRow } from './DrawingSheetList.jsx'
import SlackThreadPanel from './SlackThreadPanel.jsx'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { getProjectSheets, getProjectSheetRevisions, getCurrentDrawingSet } from '../services/sheets.js'
import { getConcepts } from '../services/design.js'
import { SpecSectionsSubSection } from './ConstructionSection.jsx'
import SchedulesSection from './SchedulesSection.jsx'
import { alertDialog } from './Dialog.jsx'

// Lazy — same flip-through viewer the contractor portal uses (pdf.js + react-pageflip are
// heavy); only loaded when someone actually opens the drawing set.
const DrawingSetViewer = lazy(() => import('./DrawingSetViewer.jsx'))

/* ── Visibility ─────────────────────────────────────────────────────────── */

function calcVisibility() {
  if (!wf.zoomProjectId) return { visible: false, isRootZoom: false, sectionZoom: null }
  const noZoom = !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
                 !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery
  const isHome     = !wf.zoomRoot && noZoom
  const isRefZoom  = wf.zoomRoot === 'reference' && noZoom
  const isProdRoot = wf.zoomRoot === 'production' && !wf.zoomSection && !wf.zoomCat && !wf.zoomItemId
  // Section zoom: sec:prod:sheets, sec:prod:disc:*, sec:prod:sheet:*.
  const sec = wf.zoomSection || ''
  const isSectionZoom = wf.zoomRoot === 'production' && (
    sec === 'sec:prod:sheets' || sec === 'sec:prod:specs' ||
    sec.startsWith('sec:prod:disc:') || sec.startsWith('sec:prod:sheet:')
  )
  // sectionZoom: 'sheets', 'disc:Architectural', 'sheet:{sheetId}(:{subKey})'.
  const sectionZoom = isSectionZoom ? sec.slice('sec:prod:'.length) : null
  return { visible: isHome || isRefZoom || isProdRoot || isSectionZoom, isRootZoom: isProdRoot, isRefZoom, sectionZoom }
}

/* ── Main component ─────────────────────────────────────────────────────── */

export default function ProductionSection() {
  const [sheets, setSheets] = useState(null)
  const [showAddWizard, setShowAddWizard] = useState(false)
  const [wizardMilestoneId, setWizardMilestoneId] = useState(null)
  const [wizardAllowedDisciplines, setWizardAllowedDisciplines] = useState(null)
  const [wizardNewSheetsOnly, setWizardNewSheetsOnly] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [recordId, setRecordId] = useState(() => wf.zoomProjectId || null)
  const [approvedConceptId, setApprovedConceptId] = useState(null)
  const [{ visible, isRootZoom, isRefZoom, sectionZoom }, setVisState] = useState(calcVisibility)
  // "View drawing set" — the flip-through viewer, same as the contractor portal's. null = closed;
  // array = open with that resolved set. Loading is a separate flag so the menu item can show
  // "Loading…" without needing a whole extra modal for it.
  const [viewerDrawings, setViewerDrawings] = useState(null)
  const [viewerLoading, setViewerLoading] = useState(false)
  /* forceUpdate — re-reads wf.collapsed on every outline-render so /collapse works */
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  /* ActionMenu for the Drawing Sheets section header — same module as DisciplineGroup */
  const sheetsMenu = useActionMenu()

  useEffect(() => {
    if (!recordId) return
    setSheets(null)

    getProjectSheets(recordId)
      .then(records => {
        setSheets(records)
        // Mirror into ctx.projSheetCache so the outline engine's breadcrumb and ZoomTitle
        // can resolve a sheet's number/title when zooming into a sheet from Production
        // (otherwise the zoom title falls back to the raw record id). Dispatch a render so a
        // deep-linked sheet zoom updates its title once the list resolves.
        ctx.projSheetCache = records
        window.dispatchEvent(new Event('outline-render'))
        const ids = records.map(s => s.id)
        // Pre-populate sheetRevCache so PDF icons show without requiring expansion.
        // Batch-fetch all revisions for this project's sheets and cache by sheet ID.
        if (ids.length > 0 && ctx.sheetRevCache) {
          getProjectSheetRevisions(ids)
            .then(revisions => {
              if (!ctx.sheetRevCache) return
              // Group revisions by sheet ID (Drawing Sheet is multipleRecordLinks → real IDs)
              const bySheet = {}
              revisions.forEach(r => {
                const pdf = r.fields?.PDF
                // Only cache if the revision has fields we care about
                const sids = r.fields?.['Drawing Sheet'] || []
                sids.forEach(sid => {
                  if (!bySheet[sid]) bySheet[sid] = []
                  bySheet[sid].push(r)
                })
              })
              // Write to cache (skip sheets already fetched individually)
              ids.forEach(sid => {
                if (ctx.sheetRevCache[sid] === undefined) {
                  ctx.sheetRevCache[sid] = bySheet[sid] || []
                }
              })
              window.dispatchEvent(new Event('outline-render'))
            })
            .catch(() => {}) // non-critical — icons will load lazily on expand if this fails
        }
      })
      .catch(() => setSheets([]))

    // Approved concept — the Specifications branch (now under Production) is concept-gated.
    if (Array.isArray(ctx.conceptCache)) {
      const a = ctx.conceptCache.find(c => c.fields && c.fields['Selected Concept'] === true)
      setApprovedConceptId(a ? a.id : null)
    } else {
      getConcepts(recordId).then(list => {
        ctx.conceptCache = list
        const a = list.find(c => c.fields && c.fields['Selected Concept'] === true)
        setApprovedConceptId(a ? a.id : null)
      }).catch(() => setApprovedConceptId(null))
    }
  }, [recordId, refreshKey])

  useEffect(() => {
    function onRender() {
      const newRecordId = wf.zoomProjectId || null
      setRecordId(prev => prev !== newRecordId ? newRecordId : prev)
      setVisState(calcVisibility())
      forceUpdate() /* re-reads wf.collapsed so /collapse and external state changes reflect */
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // A sheet's number/title/template was edited (any context) — those feed the Airtable
    // formulas groupByDiscipline() sorts by, so re-fetch rather than trust stale local state.
    function onRefreshProjSheets() { setRefreshKey(k => k + 1) }
    window.addEventListener('aj-refresh-proj-sheets', onRefreshProjSheets)
    return () => window.removeEventListener('aj-refresh-proj-sheets', onRefreshProjSheets)
  }, [])

  useEffect(() => {
    function onOpenAddSheets(e) {
      setWizardMilestoneId(e.detail?.msId || null)
      setWizardAllowedDisciplines(e.detail?.allowedDisciplines || null)
      setWizardNewSheetsOnly(!!(e.detail?.newSheetsOnly))
      setShowAddWizard(true)
    }
    window.addEventListener('open-add-sheets', onOpenAddSheets)
    return () => window.removeEventListener('open-add-sheets', onOpenAddSheets)
  }, [])

  const wizard = showAddWizard && (
    <AddSheetsWizard
      projectId={recordId}
      milestones={ctx.state?.milestones || []}
      initialMilestoneId={wizardMilestoneId}
      allowedDisciplines={wizardAllowedDisciplines}
      newSheetsOnly={wizardNewSheetsOnly}
      onClose={() => setShowAddWizard(false)}
      onCreated={() => {
        setShowAddWizard(false)
        setRefreshKey(k => k + 1)
        if (wizardMilestoneId && ctx.refreshMsSheets) ctx.refreshMsSheets(wizardMilestoneId)
      }}
    />
  )

  const drawingSetViewerNode = viewerDrawings && (
    <Suspense fallback={null}>
      <DrawingSetViewer drawings={viewerDrawings} onClose={() => setViewerDrawings(null)} />
    </Suspense>
  )

  if (!visible) return wizard || null

  /* ── Collapse state — read directly from wf so /collapse and external changes reflect ── */
  const rootCollapsed   = wf.collapsed['root:production']  !== false
  const sheetsCollapsed = wf.collapsed['sec:prod:sheets']  !== false

  /* ── Section content (children only — used in zoom mode and inside section nodes) ── */
  /* ZOOM RULE: In section-zoom mode, render sheetsContent directly — NOT the
     full section node. ZoomTitle already shows the section name in the breadcrumb/title
     bar; rendering the section header again produces a duplicate title. */

  // Derive project name from loaded milestones (best available source without a separate fetch)
  const projectName = ctx.state?.milestones?.[0]?.fields?.['Project Name'] || ''

  // Helper: normalize a Drawing Sheet Airtable record for DrawingSheetList
  function normalizeSheet(s) {
    const f = s.fields || {}
    const disc = Array.isArray(f['Discipline']) ? f['Discipline'][0] : f['Discipline']
    // multipleLookupValues of the template's PDF Insert Type link → returns names
    const insertTypeArr = f['PDF Insert Type (from Drawing Sheet Template)']
    const insertTypeName = Array.isArray(insertTypeArr) ? (insertTypeArr[0] || '') : (insertTypeArr || '')
    return {
      id:             s.id,
      title:          f['Sheet Number and Title'] || '',
      sheetNumber:    f['Sheet Number']  || '',
      sheetTitle:     f['Sheet Title']   || '',
      progress:       f['Sheet Progress'] || '',
      slackThreadTs:  f['Slack Thread Timestamp'] || '',
      templateId:     (Array.isArray(f['Drawing Sheet Template']) ? f['Drawing Sheet Template'][0] : f['Drawing Sheet Template']) || '',
      insertTypeName: insertTypeName || '',
      // Grouping + ordering (matches the firm's reference sheet list).
      discipline:     (disc || '').trim(),
      disciplineSort: f['Sheet Discipline Sort Order'],
      sortOrder:      f['Drawing Sort Order'] || f['Sheet Sort Order'],
    }
  }

  const sheetsContent = (
    <>
      {!sheets && <div className="wf-loading">Loading sheets...</div>}
      {sheets && sheets.length === 0 && (
        <div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>No drawing sheets</div>
      )}
      {sheets && sheets.length > 0 && (
        <DrawingSheetList
          sheets={sheets.map(normalizeSheet)}
          collapseKeyPrefix="sec:prod:disc"
          context="production"
          currentMemberId={wf.currentMemberId}
          onDisciplineZoom={discipline => ctx.zoomToSection('sec:prod:disc:' + discipline, { root: 'production' })}
          onSheetZoom={sheetId => ctx.zoomToSection('sec:prod:sheet:' + sheetId, { root: 'production' })}
          projectId={recordId}
          projectName={projectName}
        />
      )}
    </>
  )

  /* ── Expand / collapse all disciplines in the Drawing Sheets section ── */
  function sheetsExpandAll() {
    if (!sheets) return
    const discs = [...new Set(sheets.map(s => getDiscipline(s.fields['Sheet Number and Title'] || '')))]
    discs.forEach(d => { wf.collapsed['sec:prod:disc:' + d] = false })
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }
  function sheetsCollapseAll() {
    if (!sheets) return
    const discs = [...new Set(sheets.map(s => getDiscipline(s.fields['Sheet Number and Title'] || '')))]
    discs.forEach(d => { wf.collapsed['sec:prod:disc:' + d] = true })
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  /* ── "View drawing set" — same flip-through viewer as the contractor portal, reached in-app
     instead of a token link. Resolves the current (latest posted) set server-side. ── */
  async function openDrawingSetViewer() {
    if (!recordId || viewerLoading) return
    setViewerLoading(true)
    try {
      const drawings = await getCurrentDrawingSet(recordId)
      if (!drawings.length) {
        await alertDialog({ title: 'No drawings yet', message: 'No posted drawing sheets for this project yet.' })
        return
      }
      setViewerDrawings(drawings)
    } catch (e) {
      await alertDialog({ title: 'Could not load the drawing set', message: (e && e.message) || 'Something went wrong.' })
    } finally { setViewerLoading(false) }
  }

  /* ── Shared section markup (header + children — used in non-zoomed views only) ── */

  const sheetsSection = (
    <div className={`wf-node wf-node--section wf-node--section-sheets${sheetsCollapsed ? ' wf-node--collapsed' : ''}`} data-key="sec:prod:sheets">
      <div className="wf-row">
        <button
          className="wf-triangle"
          onClick={() => {
            wf.collapsed['sec:prod:sheets'] = !sheetsCollapsed
            saveViewState()
            window.dispatchEvent(new Event('outline-render'))
          }}
        >
          <span className={`wf-tri-icon ${sheetsCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--section"
          title="Focus on Drawing Sheets"
          onClick={() => ctx.zoomToSection('sec:prod:sheets')}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Drawing Sheets</span>
          {sheets && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: '8px' }}>{sheets.length}</span>}
        </div>
        {/* React ActionMenu — same module as DisciplineGroup for visual parity */}
        <ActionMenuButton menu={sheetsMenu} />
      </div>
      <ActionMenu menu={sheetsMenu}>
        <ActionMenuItem onClick={sheetsExpandAll}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={sheetsCollapseAll}>Collapse all</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => window.dispatchEvent(new Event('enter-select-sheets'))}>
          Select sheets for export
        </ActionMenuItem>
        <ActionMenuItem onClick={openDrawingSetViewer}>View drawing set</ActionMenuItem>
        {/* Drawing sheets are added only from the milestone tree (bug 3) — no Add here in Reference. */}
      </ActionMenu>
      {!sheetsCollapsed && (
        <div className="wf-children">
          {sheetsContent}
        </div>
      )}
    </div>
  )

  /* ── Specifications branch (Round 12.5 — relocated under Production) ──────── */
  const specsSection = (
    <SpecSectionsSubSection projectId={recordId} conceptId={approvedConceptId} />
  )

  /* ── Schedules branch (Round 17 — peer of Specifications under Production) ── */
  const schedulesSection = (
    <SchedulesSection projectId={recordId} conceptId={approvedConceptId} />
  )

  /* ── Section zoom — render only the targeted section (ZoomTitle shows breadcrumb) */
  if (sectionZoom) {
    // Sheet-level zoom: sec:prod:sheet:{sheetId} or sec:prod:sheet:{sheetId}:cl
    if (sectionZoom.startsWith('sheet:')) {
      const rest     = sectionZoom.slice('sheet:'.length)
      const colonIdx = rest.indexOf(':')
      const sheetId  = colonIdx !== -1 ? rest.slice(0, colonIdx) : rest
      const subKey   = colonIdx !== -1 ? rest.slice(colonIdx + 1) : null
      const rawSheet = sheets?.find(s => s.id === sheetId)
      const targetSheet = rawSheet ? normalizeSheet(rawSheet) : null
      return (
        <>
          <div className="wf-list">
            {(!sheets || !targetSheet) && <div className="wf-loading">Loading sheet...</div>}
            {targetSheet && (
              <SheetRow
                sheet={targetSheet}
                context="production"
                currentMemberId={wf.currentMemberId}
                zoomedAs={subKey === 'cl' ? 'cl' : 'sheet'}
                projectId={recordId}
                projectName={projectName}
              />
            )}
          </div>
          {wizard}
        </>
      )
    }
    // Discipline-level zoom: only show sheets for that discipline
    if (sectionZoom.startsWith('disc:')) {
      const filterDiscipline = sectionZoom.slice('disc:'.length)
      return (
        <>
          <div className="wf-list">
            {!sheets && <div className="wf-loading">Loading sheets...</div>}
            {sheets && (
              <DrawingSheetList
                sheets={sheets.map(normalizeSheet)}
                collapseKeyPrefix="sec:prod:disc"
                context="production"
                currentMemberId={wf.currentMemberId}
                onSheetZoom={sheetId => ctx.zoomToSection('sec:prod:sheet:' + sheetId, { root: 'production' })}
                filterDiscipline={filterDiscipline}
                projectId={recordId}
                projectName={projectName}
              />
            )}
          </div>
          {wizard}
        </>
      )
    }
    /* Section-zoom: render content only — ZoomTitle already shows the section name */
    return (
      <>
        <div className="wf-list">
          {sectionZoom === 'sheets' ? sheetsContent
            : <SpecSectionsSubSection projectId={recordId} conceptId={approvedConceptId} zoomed />}
        </div>
        {wizard}
      </>
    )
  }

  /* ── Production root zoom — render sections directly (outline engine shows title/breadcrumb) */
  if (isRootZoom) {
    return (
      <>
        <div className="wf-list">
          {sheetsSection}
          {specsSection}
          {schedulesSection}
        </div>
        {wizard}
        {drawingSetViewerNode}
      </>
    )
  }

  /* ── Project home / Reference zoom — Production root node ──────────────── */
  // At project level the root row renders only when Reference is expanded
  // (indented); in Reference zoom it renders plain.
  if (!isRefZoom && wf.collapsed['root:reference'] !== false) return null
  return (
    <>
      <div className="wf-list">
        <div className={isRefZoom ? undefined : 'wf-ref-child'}>
        <div className={`wf-node wf-node--root wf-node--root-production${rootCollapsed ? ' wf-node--collapsed' : ''}`} data-key="root:production">
          <div className="wf-row">
            <button
              className="wf-triangle"
              onClick={() => { wf.collapsed['root:production'] = !rootCollapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }}
            >
              <span className={`wf-tri-icon ${rootCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
            </button>
            <button
              className="wf-bullet wf-bullet--root"
              title="Focus on Production"
              onClick={() => ctx.zoomToRoot && ctx.zoomToRoot('production')}
            ></button>
            <div className="wf-text-wrap">
              <span className="wf-node-text wf-node-text--root">Production</span>
            </div>
            <button
              className="wf-menu-btn"
              data-fn="openMenu"
              data-type="root"
              data-key="root:production"
              data-rootname="production"
            >&#8943;</button>
          </div>
          {!rootCollapsed && (
            <div className="wf-children">
              {sheetsSection}
              {specsSection}
              {schedulesSection}
            </div>
          )}
        </div>
        </div>
      </div>
      {wizard}
      {drawingSetViewerNode}
    </>
  )
}
